// services/communication/chatService.ts
//
// FE wrapper over the /chat/* REST endpoints. See
// edemics-api/src/communication/chat/chat.controller.ts for the source
// of truth on shapes. Realtime updates come via chatSocket.ts.

import { api, handleApiError } from './api'

// ============================================================================
// Types (mirror BE shapes; add fields as they land)
// ============================================================================

export type ChatConversationType = 'Direct' | 'Group'
export type ChatConversationRole = 'Owner' | 'Member'
export type ChatMessageContentType = 'Text' | 'Image' | 'File' | 'System'

export interface ChatUser {
  id: string
  name: string
  email?: string | null
  avatar?: string | null
  role?: string | null
}

export interface ChatConversationParticipant {
  id: string
  conversationId: string
  userId: string
  role: ChatConversationRole
  lastReadAt: string | null
  joinedAt: string
  leftAt: string | null
  user?: ChatUser
}

export interface ChatMessageAttachment {
  id: string
  messageId: string
  filename: string
  mimetype: string
  sizeBytes: number
  storagePath: string
}

export interface ChatMessageReaction {
  id: string
  messageId: string
  userId: string
  emoji: string
  createdAt: string
  user?: ChatUser
}

export interface ChatMessageReplyRef {
  id: string
  content: string
  sender?: { id: string; name: string }
}

export interface ChatMessage {
  id: string
  conversationId: string
  senderUserId: string
  content: string
  contentType: ChatMessageContentType
  replyToId: string | null
  editedAt: string | null
  deletedAt: string | null
  createdAt: string
  // V5 §1.11 #7 — urgent-message scheduler fields. Only populated
  // for messages the sender flagged urgent; stay null/false otherwise.
  isUrgent?: boolean
  urgentUntil?: string | null
  urgentIntervalSec?: number | null
  urgentAcknowledgedAt?: string | null
  urgentAcknowledgedBy?: string | null
  sender?: ChatUser
  attachments?: ChatMessageAttachment[]
  reactions?: ChatMessageReaction[]
  replyTo?: ChatMessageReplyRef | null
}

export interface ChatConversation {
  id: string
  tenantId: string
  type: ChatConversationType
  title: string | null
  createdByUserId: string
  isArchived: boolean
  lastMessageAt: string | null
  createdAt: string
  updatedAt: string
  participants?: ChatConversationParticipant[]
  messages?: ChatMessage[]
}

export interface ListMessagesResponse {
  items: ChatMessage[]
  nextCursor: string | null
}

// ============================================================================
// Users (chat picker)
// ============================================================================

/**
 * BE-side picker type — the ChatController maps every User.role
 * value into one of these five buckets so the new-chat dialog can
 * show a coherent list.
 */
export type ChatPickerUserType =
  | 'parent'
  | 'student'
  | 'staff'
  | 'supplier'
  | 'alumni'

export interface ChatPickerUser extends ChatUser {
  /** Bucket label suitable for the picker's radio/checkbox row. */
  type: ChatPickerUserType
}

/**
 * List tenant users other than the caller for the New Chat picker.
 * Backs V5 chat #1. Accepts optional `q` (name/email prefix) and
 * `type` (parent/student/staff/supplier/alumni) filters — both
 * server-side so a large tenant doesn't drag the whole directory
 * into the browser.
 */
export async function listChatUsers(
  opts: { q?: string; type?: ChatPickerUserType } = {},
): Promise<ChatPickerUser[]> {
  try {
    const { data } = await api.get<ChatPickerUser[]>('/chat/users', {
      params: opts,
    })
    return data
  } catch (e) {
    throw handleApiError(e, 'list chat users')
  }
}

// ============================================================================
// Conversations
// ============================================================================

export async function listConversations(): Promise<ChatConversation[]> {
  try {
    const { data } = await api.get<ChatConversation[]>('/chat/conversations')
    return data
  } catch (e) {
    throw handleApiError(e, 'list conversations')
  }
}

export async function getConversation(id: string): Promise<ChatConversation> {
  try {
    const { data } = await api.get<ChatConversation>(`/chat/conversations/${id}`)
    return data
  } catch (e) {
    throw handleApiError(e, 'get conversation')
  }
}

export async function createConversation(input: {
  type: ChatConversationType
  title?: string
  participantUserIds: string[]
}): Promise<ChatConversation> {
  try {
    const { data } = await api.post<ChatConversation>('/chat/conversations', input)
    return data
  } catch (e) {
    throw handleApiError(e, 'create conversation')
  }
}

export async function updateConversation(
  id: string,
  patch: { title?: string; isArchived?: boolean },
): Promise<ChatConversation> {
  try {
    const { data } = await api.patch<ChatConversation>(`/chat/conversations/${id}`, patch)
    return data
  } catch (e) {
    throw handleApiError(e, 'update conversation')
  }
}

export async function addParticipants(
  id: string,
  userIds: string[],
): Promise<ChatConversation> {
  try {
    const { data } = await api.post<ChatConversation>(
      `/chat/conversations/${id}/participants`,
      { userIds },
    )
    return data
  } catch (e) {
    throw handleApiError(e, 'add participants')
  }
}

export async function removeParticipant(
  id: string,
  userId: string,
): Promise<ChatConversation> {
  try {
    const { data } = await api.delete<ChatConversation>(
      `/chat/conversations/${id}/participants/${userId}`,
    )
    return data
  } catch (e) {
    throw handleApiError(e, 'remove participant')
  }
}

// ============================================================================
// Messages
// ============================================================================

export async function listMessages(
  conversationId: string,
  opts: { cursor?: string; limit?: number } = {},
): Promise<ListMessagesResponse> {
  try {
    const { data } = await api.get<ListMessagesResponse>(
      `/chat/conversations/${conversationId}/messages`,
      { params: opts },
    )
    return data
  } catch (e) {
    throw handleApiError(e, 'list messages')
  }
}

export async function sendMessage(
  conversationId: string,
  input: {
    content: string
    contentType?: ChatMessageContentType
    replyToId?: string
    attachments?: Array<{
      filename: string
      mimetype: string
      sizeBytes: number
      storagePath: string
    }>
  },
): Promise<ChatMessage> {
  try {
    const { data } = await api.post<ChatMessage>(
      `/chat/conversations/${conversationId}/messages`,
      input,
    )
    return data
  } catch (e) {
    throw handleApiError(e, 'send message')
  }
}

export async function editMessage(
  id: string,
  content: string,
): Promise<ChatMessage> {
  try {
    const { data } = await api.patch<ChatMessage>(`/chat/messages/${id}`, { content })
    return data
  } catch (e) {
    throw handleApiError(e, 'edit message')
  }
}

export async function deleteMessage(id: string): Promise<{ id: string; deletedAt: string }> {
  try {
    const { data } = await api.delete<{ id: string; deletedAt: string }>(
      `/chat/messages/${id}`,
    )
    return data
  } catch (e) {
    throw handleApiError(e, 'delete message')
  }
}

export async function addReaction(
  messageId: string,
  emoji: string,
): Promise<ChatMessageReaction> {
  try {
    const { data } = await api.post<ChatMessageReaction>(
      `/chat/messages/${messageId}/reactions`,
      { emoji },
    )
    return data
  } catch (e) {
    throw handleApiError(e, 'add reaction')
  }
}

export async function removeReaction(messageId: string, emoji: string): Promise<void> {
  try {
    await api.delete(`/chat/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`)
  } catch (e) {
    throw handleApiError(e, 'remove reaction')
  }
}

export async function markRead(
  conversationId: string,
  opts: { upToMessageId?: string } = {},
): Promise<{ conversationId: string; userId: string; lastReadAt: string }> {
  try {
    const { data } = await api.post<{
      conversationId: string
      userId: string
      lastReadAt: string
    }>(`/chat/conversations/${conversationId}/mark-read`, opts)
    return data
  } catch (e) {
    throw handleApiError(e, 'mark read')
  }
}

export async function sendTyping(
  conversationId: string,
  isTyping: boolean,
): Promise<void> {
  try {
    await api.post(`/chat/conversations/${conversationId}/typing`, { isTyping })
  } catch (e) {
    // Typing is fire-and-forget — swallow errors silently
  }
}

// ============================================================================
// V5 §1.11 #7 — Urgent Message scheduler
// ============================================================================

export interface MarkUrgentInput {
  /** Clamp 1-30 on the BE. Omit to use CommunicationSettings default. */
  intervalMinutes?: number
  /** Clamp 1-120 on the BE. Omit to use CommunicationSettings default. */
  durationMinutes?: number
}

export interface MarkUrgentResponse {
  id: string
  isUrgent: true
  urgentUntil: string
  urgentIntervalSec: number
}

/**
 * Mark one's own chat message urgent. BE emits `message:urgent-started`
 * on the conversation WS room immediately and chains reminder events
 * every `urgentIntervalSec` seconds until acknowledged or expired.
 */
export async function markChatMessageUrgent(
  messageId: string,
  input: MarkUrgentInput = {},
): Promise<MarkUrgentResponse> {
  try {
    const { data } = await api.post<MarkUrgentResponse>(
      `/chat/messages/${messageId}/urgent`,
      input,
    )
    return data
  } catch (e) {
    throw handleApiError(e, 'mark message urgent')
  }
}

/**
 * Acknowledge an urgent chat message. Any conversation participant
 * can ack; BE emits `message:urgent-acknowledged` on the room so
 * every open panel can drop the banner immediately. Idempotent.
 */
export async function acknowledgeChatMessageUrgent(
  messageId: string,
): Promise<{ id: string; urgentAcknowledgedAt: string }> {
  try {
    const { data } = await api.post<{ id: string; urgentAcknowledgedAt: string }>(
      `/chat/messages/${messageId}/acknowledge-urgent`,
    )
    return data
  } catch (e) {
    throw handleApiError(e, 'acknowledge urgent message')
  }
}
